import { UserType } from '@/Interface/userType';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { of, Subject, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { UserPermissionsModalComponent } from './user-permissions-modal/user-permissions-modal.component';
import { PermissionsService } from '@services/permissions.service';
import { DEFAULT_PERMISSIONS } from '@/constants/constants';

@Component({
  selector: 'app-user-role',
  templateUrl: './user-role.component.html',
  styleUrls: ['./user-role.component.scss']
})
export class UserRoleComponent implements OnInit {
  public userRoleForm: FormGroup;
  public ruleForm: FormGroup;
  public pageSizeControl = new FormControl(10);
  pageSize: number;
  pageToGo: number = 1;
  searchElement = '';
  serialNumberArray: Array<number> = [];
  next_page: number;
  current_page: number;
  previous_page: number;
  total_pages: number;
  total_items: number;
  private latestQueryParams: any = {};
  sortColumn: string = null;
  sortOrder: 'asc' | 'desc' = null;
  searchSubject: Subject<string> = new Subject();
  loader: boolean = false;
  // Separate from `loader`: this one is scoped to the table, so a filter,
  // a search keystroke or a page change no longer blanks the whole page.
  tableLoader: boolean = false;
  isEditMode: boolean = false;
  isViewMode: boolean = false;

  public pageRecordOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
  ];

  public userTypeList: any[] = [];
  public typeDropdownList: UserType[] = [];
  public filteredSubordinateList: UserType[] = [];
  public existingRules: any = [];
  public selectedId: number;
  public selectedParentType: number = null;
  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady: boolean = false;
  @ViewChild(UserPermissionsModalComponent) permissionsModal!: UserPermissionsModalComponent;
  constructor(
    private modalService: NgbModal,
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private permService: PermissionsService,
  ) { }

  ngOnInit(): void {
    this.initComponent();
    // this.getComponentPermissions()
    // this.fetchUserTypeDropdown();
    // this.initializeForm();
    // this.initializeRuleForm();
    // this.getTableData();
    // this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
    //   this.searchElement = searchTerm;
    //   this.updateQueryParams({ search: this.searchElement });
    // });
  }
  async initComponent() {
    this.loader = true
    await this.getComponentPermissions();

    this.fetchUserTypeDropdown();
    this.initializeForm();
    this.initializeRuleForm();
    this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
      this.searchElement = searchTerm;
      this.updateQueryParams({ search: this.searchElement });
    });
    if (this.permissions.canView) {
      this.getTableData();
    }
    setTimeout(() => {
        this.loader = false;
    }, 250);
  }

  async getComponentPermissions() {
    const url = this.permService.getCurrentUrl();
    // const menuFromLocal = this.permService.loadMenuFromLocalStorage();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();

    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }
  get hasAnyActionPermission(): boolean {
    return (
      this.permissionsReady &&
      (this.permissions.canView ||
        this.permissions.canEdit ||
        this.permissions.canDelete)
    );
  }


  initializeForm(): void {
    this.userRoleForm = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required, Validators.minLength(2)]],
      user_type: [null, [Validators.required]],
      status: [true, [Validators.required]],
      description: ['']
    });
  }

  initializeRuleForm(): void {
    this.ruleForm = this.formBuilder.group({
      parentUserType: [null, [Validators.required]],
      subordinateUserTypes: [[], [Validators.required, this.minLengthArray(1)]]
    });

    this.ruleForm.get('parentUserType').valueChanges.subscribe((parentId) => {
      this.onParentTypeChange(parentId);
    });
  }

  minLengthArray(min: number) {
    return (control: FormControl) => {
      if (control.value && control.value.length >= min) {
        return null;
      }
      return { minLengthArray: { valid: false } };
    };
  }

  onParentTypeChange(parentId: number): void {
    this.selectedParentType = parentId;
    this.ruleForm.patchValue({ subordinateUserTypes: [] }, { emitEvent: false });
    this.filterSubordinateList(parentId);
  }
  public selectTypelistStatus(selectedTypeId) {
    this.updateQueryParams({ user_type: selectedTypeId });
  }

  filterSubordinateList(parentId: number): void {
    if (!parentId) {
      this.filteredSubordinateList = [];
      return;
    }

    const assignedSubordinates = this.existingRules
      .filter(rule => rule.creator_type === parentId)
      .map(rule => rule.can_create_type);

    this.filteredSubordinateList = this.typeDropdownList.filter(
      type => type.id !== parentId && !assignedSubordinates.includes(type.id)
    );
  }

  resetForm(): void {
    this.userRoleForm.reset({
      id: null,
      name: '',
      user_type: null,
      status: true,
      description: ''
    });
    this.isEditMode = false;
    this.isViewMode = false;
  }

  resetRuleForm(): void {
    this.ruleForm.reset({
      parentUserType: null,
      subordinateUserTypes: []
    });
    this.selectedParentType = null;
    this.filteredSubordinateList = [];
  }

  deleteRule(ruleId: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this hierarchy rule?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FB8C04',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        const ruleToDelete = this.existingRules.find(rule => rule.id === ruleId);
        this.existingRules = this.existingRules.filter(rule => rule.id !== ruleId);

        this._utilityService.deleteHierarchyRule(ruleId).subscribe(() => {
          this.toaster.success('Rule deleted successfully');
        });

        if (this.selectedParentType === ruleToDelete?.creator_type) {
          this.filterSubordinateList(this.selectedParentType);
        }
      }
    });
  }

  onSubmitRule(): void {
    Object.keys(this.ruleForm.controls).forEach(key => {
      this.ruleForm.get(key).markAsTouched();
    });

    if (this.ruleForm.valid) {
      const parentType = this.ruleForm.value.parentUserType;
      const subordinateTypes = this.ruleForm.value.subordinateUserTypes;

      const rules = subordinateTypes.map(subordinateId => ({
        creator_type: parentType,
        can_create_type: subordinateId
      }));

      const payload = { rules };

      this._utilityService.createHierarchyRules(payload).subscribe(
        (response) => {
          this.toaster.success('Rules created successfully');
          this.fetchUserCreationHierarchy();
          this.resetRuleForm();
        },
        (error) => {
          this.toaster.error('Failed to create rules');
        }
      );
    }
  }

  openAddPopUp(content: any): void {
    this.isEditMode = false;
    this.isViewMode = false;
    this.resetForm();
    this.modalService.open(content, { centered: true, scrollable: true });
  }

  openEditPopUp(item: any, content: any): void {
    this.isEditMode = true;
    this.isViewMode = false;
    this.selectedId = item.id;
    this.userRoleForm.patchValue({
      id: item.id,
      name: item.name,
      user_type: item.user_type,
      status: item.status,
      description: item.description || ''
    });
    this.userRoleForm.get('user_type').disable()
    this.modalService.open(content, { centered: true, scrollable: true }).result.then(
      () => {
        this.userRoleForm.enable();
        this.resetForm();
      },
      () => {
        this.userRoleForm.enable();
        this.resetForm();
      }
    );
  }

  openViewPopUp(item: any, content: any): void {
    this.isEditMode = false;
    this.isViewMode = true;
    this.selectedId = item.id;
    this.userRoleForm.patchValue({
      id: item.id,
      name: item.name,
      user_type: item.user_type,
      status: item.status,
      description: item.description || ''
    });
    this.userRoleForm.disable();
    this.modalService.open(content, { centered: true, scrollable: true }).result.then(
      () => {
        this.userRoleForm.enable();
        this.resetForm();
      },
      () => {
        this.userRoleForm.enable();
        this.resetForm();
      }
    );
  }

  onSubmit(): void {
    if (this.userRoleForm.valid) {
      if (this.isEditMode) {
        this.updateUserRole();
      } else {
        this.createUserRole();
      }
    } else {
      Object.keys(this.userRoleForm.controls).forEach(key => {
        this.userRoleForm.get(key).markAsTouched();
      });
    }
  }

  createUserRole(): void {
    const payload = {
      name: this.userRoleForm.value.name.trim(),
      user_type: this.userRoleForm.value.user_type,
      status: this.userRoleForm.value.status,
      description: this.userRoleForm.value.description?.trim() || null
    };

    this.loader = true;

    this._utilityService.createUserRole(payload).pipe(
      catchError((error: HttpErrorResponse) => {
        this.loader = false;
        this.handleApiFieldErrors(error, this.toastr);
        return of(undefined);
      })
    ).subscribe((response: any) => {
      if (response) {
        this.loader = false;
        this.toastr.success('Role Created Successfully');
        this.resetForm();
        this.modalService.dismissAll();
        this.getTableData();
      }
    });
  }

  updateUserRole(): void {
    Swal.fire({
      title: 'Are you sure you want to update this record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FB8C04',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Update it!'
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          id: this.userRoleForm.value.id,
          name: this.userRoleForm.value.name.trim(),
          user_type: this.userRoleForm.value.user_type,
          status: this.userRoleForm.value.status,
          description: this.userRoleForm.value.description?.trim() || null
        };

        this.loader = true;

        this._utilityService.updateUserRole(this.selectedId, payload).pipe(
          catchError((error: HttpErrorResponse) => {
            this.loader = false;
            this.handleApiFieldErrors(error, this.toastr);
            return of(undefined);
          })
        ).subscribe((response: any) => {
          if (response) {
            this.loader = false;
            this.toastr.success('Role Updated Successfully');
            this.resetForm();
            this.modalService.dismissAll();
            this.getTableData();
          }
        });
      }
    });
  }

  toggleUserRoleStatus(item: any): void {
    const newStatus = !item.status;
    const payload = {
      id: item.id,
      name: item.name,
      user_type: item.user_type,
      status: newStatus,
      description: item.description
    };

    this._utilityService.updateUserRole(item.id, payload).pipe(
      catchError(error => {
        this.toaster.error('Failed to update status');
        return throwError(() => error);
      })
    ).subscribe(response => {
      if (response && response.message) {
        this.getTableData();
        this.toaster.success(response.message);
      }
    });
  }
  private updateQueryParams(paramsToUpdate: any): void {
    this.latestQueryParams = {
      ...this.latestQueryParams,
      ...paramsToUpdate
    };

    // ---- CLEAN DEFAULT / EMPTY VALUES ----
    if (this.latestQueryParams.search === '') {
      this.latestQueryParams.search = null;
    }

    if (this.latestQueryParams.page === 1) {
      this.latestQueryParams.page = null;
    }

    if (this.latestQueryParams.size === 10) {
      this.latestQueryParams.size = null;
    }

    if (this.latestQueryParams.sort === null) {
      delete this.latestQueryParams.sort;
    }

    if (this.latestQueryParams.column === null) {
      delete this.latestQueryParams.column;
    }

    // 🔹 USER TYPE CLEANUP
    if (
      this.latestQueryParams.user_type === null ||
      this.latestQueryParams.user_type === ''
    ) {
      delete this.latestQueryParams.user_type;
    }

    // ---- PAGE RESET LOGIC ----
    if (
      paramsToUpdate.search !== undefined ||
      paramsToUpdate.size !== undefined ||
      paramsToUpdate.user_type !== undefined
    ) {
      // Reset page when filters change
      this.latestQueryParams.page = null;
      this.latestQueryParams.user_type !== undefined
    }

    // ---- EDGE CASE: NO NEXT PAGE ----
    if (!this.next_page && (paramsToUpdate.size || paramsToUpdate.search || paramsToUpdate.user_type)) {
      this.latestQueryParams = {
        size: paramsToUpdate.size || null,
        search: paramsToUpdate.search || null,
        user_type: paramsToUpdate.user_type || null
      };
    }

    this.getTableData();
  }

  // private updateQueryParams(paramsToUpdate: any): void {
  //   this.latestQueryParams = {
  //     ...this.latestQueryParams,
  //     ...paramsToUpdate
  //   };

  //   if (this.latestQueryParams.search === '') {
  //     this.latestQueryParams.search = null;
  //   }
  //   if (this.latestQueryParams.page === 1) {
  //     this.latestQueryParams.page = null;
  //   }
  //   if (this.latestQueryParams.size === 10) {
  //     this.latestQueryParams.size = null;
  //   }
  //   if (this.latestQueryParams.sort === null) {
  //     delete this.latestQueryParams.sort;
  //   }
  //   if (this.latestQueryParams.column === null) {
  //     delete this.latestQueryParams.column;
  //   }

  //   if (this.latestQueryParams.page === 1 || (this.latestQueryParams.page && (this.latestQueryParams.search))) {
  //     if (this.latestQueryParams.page > 1) {
  //       this.latestQueryParams.page = paramsToUpdate.page;
  //       if (this.latestQueryParams.size > 10) {
  //         this.latestQueryParams.size = paramsToUpdate.size;
  //       }
  //     } else {
  //       this.latestQueryParams.page = null;
  //     }
  //   }

  //   if (!this.next_page && (paramsToUpdate.size || paramsToUpdate.search)) {
  //     this.latestQueryParams = {
  //       size: paramsToUpdate.size || null,
  //       search: paramsToUpdate.search || null
  //     };
  //   }
  //   this.getTableData();
  // }

  private getTableData(): void {
    const name = this.latestQueryParams['search'] || this.searchElement;
    const page = this.latestQueryParams['page'] ? +this.latestQueryParams['page'] : 1;
    const size = this.latestQueryParams['size'] ? +this.latestQueryParams['size'] : 10;
    const sort = this.latestQueryParams['sort'] || null;
    const column = this.latestQueryParams['column'] || null;
    const user_type = this.latestQueryParams['user_type'] || null;
    this.getRolesList(name, page, size, sort, column, user_type);
  }

  getRolesList(name: string, page: number, size: number, sort: string | null, column: string | null, user_type: number | null): void {
    this.tableLoader = true;
    this.userTypeList = [];
    this.serialNumberArray = [];
    this._utilityService
      .getRolesListPaginated(name, page, size, sort, column, user_type)
      .subscribe(
        (res: any) => {
          if (res && res.data) {
            this.userTypeList = res.data.map((item: any, index: number) => ({
              ...item,
              srNo: (res.current_page - 1) * size + index + 1
            }));

            this.current_page = res.current_page;
            this.next_page = res.next_page;
            this.previous_page = res.previous_page;
            this.total_pages = res.total_pages;
            this.total_items = res.total_users;

            this.enablePagination();
          }

          this.tableLoader = false;
        },
        () => {
          this.tableLoader = false;
          this.toaster.error('Failed to load data');
        }
      );
  }

  public searchData(event: Event): void {
    this.searchElement = (event.target as HTMLInputElement)?.value || '';
    this.searchSubject.next(this.searchElement);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.total_pages) {
      this.updateQueryParams({ page: page });
    }
  }

  navigatePage(direction: string): void {
    if (direction === 'prev' && this.current_page > 1) {
      this.goToPage(this.current_page - 1);
    } else if (direction === 'next' && this.next_page !== null) {
      this.goToPage(this.current_page + 1);
    }
  }

  navigateToPage(): void {
    if (this.pageToGo >= 1 && this.pageToGo <= this.total_pages) {
      this.goToPage(this.pageToGo);
    }
  }

  public changePageSize(): void {
    this.pageSize = this.pageSizeControl.value!;
    this.updateQueryParams({ size: this.pageSize });
  }

  enablePagination(): void {
    const linkElement = document.getElementById('prevPage');
    if (linkElement) {
      if (this.previous_page === null) {
        linkElement.setAttribute('disabled', 'true');
        linkElement.style.pointerEvents = 'none';
        linkElement.style.color = 'gray';
      } else {
        linkElement.removeAttribute('disabled');
        linkElement.style.pointerEvents = 'auto';
        linkElement.style.color = '';
      }
    }
  }

  fetchUserTypeDropdown(): void {
    this._utilityService.getUserTypesDropdown().subscribe((res) => {
      if (res) {
        this.typeDropdownList = res;
      }
    });
  }

  fetchUserCreationHierarchy(): void {
    this._utilityService.getUserCreationHierarchyRules().subscribe((res) => {
      if (res) {
        this.existingRules = res;
      }
    });
  }

  handleApiFieldErrors(error: HttpErrorResponse, toastr: ToastrService): void {

    // Case 1: status is an array
    if (error.error?.status && Array.isArray(error.error.status)) {
      error.error.status.forEach((msg: string) => {
        toastr.error(msg);
      });
      return;
    }
    // Case 2: { error: "string" }
    if (error.error?.error && typeof error.error.error === 'string') {
      toastr.error(error.error.error);
      return;
    }

    // Case 2: single message
    if (error.error?.message) {
      toastr.error(error.error.message);
      return;
    }

    // Case 3: field validation errors
    if (error.error?.errors) {
      const errors = error.error.errors;
      Object.keys(errors).forEach(key => {
        const message = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
        toastr.error(message);
      });
      return;
    }

    // Fallback
    toastr.error('Sorry, something went wrong. Please try again.');
  }

  openPermissionsModal(roleId: number, role?: any): void {
    this.permissionsModal.openModal(roleId, role);
  }
}